import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, FloppyDisk, Receipt, LinkSimple, X } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Sidebar } from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function NewDossier() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    client_name: "", client_email: "", property_address: "", price: "", payment_moment: "a_la_commande",
    send_code_email: true, requires_validation: true, comment: "",
  });
  const [loading, setLoading] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [billitOpen, setBillitOpen] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [invLoading, setInvLoading] = useState(false);
  const [invSearch, setInvSearch] = useState("");
  const [prefill, setPrefill] = useState(null);
  const [prefilling, setPrefilling] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openBillit = async () => {
    setBillitOpen(true);
    setInvLoading(true);
    try {
      const { data } = await api.get("/billit/invoices");
      setInvoices(data);
    } catch (err) {
      toast.error("Impossible de charger les factures Billit");
    } finally {
      setInvLoading(false);
    }
  };

  const chooseInvoice = async (inv) => {
    setSelectedInvoice(inv);
    setBillitOpen(false);
    setPrefilling(true);
    try {
      const { data } = await api.get(`/billit/invoices/${inv.order_id}/prefill`);
      setPrefill(data);
      setForm((f) => ({
        ...f,
        client_name: data.client_name || f.client_name,
        client_email: data.client_email || f.client_email,
        property_address: data.property_address || f.property_address,
        price: data.price != null ? String(data.price) : f.price,
        requires_validation: data.requires_validation,
      }));
      toast.success("Données reprises de la facture Billit — vérifiez puis créez le dossier");
    } catch (err) {
      toast.error("Impossible de lire la facture — complétez manuellement");
    } finally {
      setPrefilling(false);
    }
  };

  const clearInvoice = () => { setSelectedInvoice(null); setPrefill(null); };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const payload = { ...form, price: parseFloat(form.price) };
    if (selectedInvoice) {
      payload.billit_order_id = selectedInvoice.order_id;
      payload.billit_order_number = selectedInvoice.order_number;
    }
    try {
      const { data } = await api.post("/dossiers", payload);
      toast.success(selectedInvoice ? "Dossier créé — facture envoyée au client" : "Dossier créé");
      navigate(`/admin/dossier/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar />
      <main className="ml-64 p-8 max-w-3xl" data-testid="new-dossier-page">
        <button onClick={() => navigate("/admin")} data-testid="back-button"
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-6 transition-colors">
          <ArrowLeft size={16} /> Retour aux dossiers
        </button>

        <p className="label-eyebrow mb-1">Création</p>
        <h1 className="font-heading text-3xl font-medium tracking-tight text-slate-950 mb-8">Nouveau dossier</h1>

        <form onSubmit={submit} className="bg-white border border-slate-200 rounded-md p-8 space-y-6">
          <div className="rounded-md border-2 border-blue-200 bg-blue-50/50 p-5 space-y-3" data-testid="billit-step">
            <p className="label-eyebrow">Étape 1 · Facture Billit</p>
            {selectedInvoice ? (
              <div className="flex items-center justify-between rounded-md border border-slate-200 bg-white p-3" data-testid="selected-invoice">
                <div className="flex items-center gap-2 text-sm text-slate-800 min-w-0">
                  <Receipt size={18} className="text-blue-600 shrink-0" />
                  <span className="truncate">
                    Facture <strong>{selectedInvoice.order_number}</strong>
                    {selectedInvoice.customer ? ` — ${selectedInvoice.customer}` : ""}
                    {prefill?.services ? <span className="text-slate-500"> · {prefill.services}</span> : null}
                  </span>
                </div>
                <button type="button" data-testid="remove-invoice" onClick={clearInvoice}
                  className="text-slate-400 hover:text-red-600 shrink-0"><X size={16} /></button>
              </div>
            ) : (
              <Button type="button" data-testid="link-invoice-button" onClick={openBillit}
                className="w-full h-12 bg-blue-600 hover:bg-blue-700">
                <LinkSimple size={18} className="mr-1.5" /> Choisir la facture Billit
              </Button>
            )}
            {prefilling && <p className="text-sm text-blue-700" data-testid="prefill-loading">Lecture de la facture…</p>}
            {prefill && (
              <div className="space-y-1.5 text-sm" data-testid="prefill-notes">
                <p className="text-emerald-700">✓ Nom, email, adresse du bien, prix et type de rapport repris de la facture.</p>
                {!prefill.client_email && (
                  <p className="text-amber-700" data-testid="prefill-warning-email">⚠ Aucun email client dans Billit — complétez-le ci-dessous.</p>
                )}
                {prefill.client_email && /(\.(om|con|cm|comm|nett|bee)$)|gmial|hotmial|hotmal\b/i.test(prefill.client_email) && (
                  <p className="text-red-700" data-testid="prefill-warning-email-typo">⚠ L'email « {prefill.client_email} » semble mal orthographié dans Billit — corrigez-le avant de créer le dossier.</p>
                )}
                {!prefill.property_from_invoice && (
                  <p className="text-amber-700" data-testid="prefill-warning-address">⚠ Pas d'adresse de bien sur la facture : l'adresse du client a été reprise — vérifiez-la.</p>
                )}
                {prefill.existing_dossier_id && (
                  <p className="text-red-700" data-testid="prefill-warning-existing">
                    ⚠ Cette facture est déjà liée à un dossier.{" "}
                    <a className="underline" href={`/admin/dossier/${prefill.existing_dossier_id}`}>Ouvrir le dossier existant</a>
                  </p>
                )}
              </div>
            )}
            {!selectedInvoice && (
              <p className="text-xs text-slate-500">Les données du client sont reprises automatiquement. Sans facture, encodez simplement ci-dessous.</p>
            )}
          </div>

          <p className="label-eyebrow">Étape 2 · Vérifier les informations</p>
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="cn">Nom du client</Label>
              <Input id="cn" data-testid="client-name-input" value={form.client_name}
                onChange={(e) => set("client_name", e.target.value)} className="h-11" required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ce">Email du client</Label>
              <Input id="ce" type="email" data-testid="client-email-input" value={form.client_email}
                onChange={(e) => set("client_email", e.target.value)} className="h-11" required />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="addr">Adresse du bien</Label>
            <Input id="addr" data-testid="property-address-input" value={form.property_address}
              onChange={(e) => set("property_address", e.target.value)} className="h-11"
              placeholder="Rue, numéro, code postal, ville" required />
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="price">Prix de l'expertise (€)</Label>
              <Input id="price" type="number" step="0.01" min="0" data-testid="price-input" value={form.price}
                onChange={(e) => set("price", e.target.value)} className="h-11" required />
            </div>
            <div className="space-y-2">
              <Label>Moment du paiement</Label>
              <Select value={form.payment_moment} onValueChange={(v) => set("payment_moment", v)}>
                <SelectTrigger className="h-11" data-testid="payment-moment-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="a_la_commande" data-testid="option-a-la-commande">À la commande</SelectItem>
                  <SelectItem value="lors_visite" data-testid="option-lors-visite">Lors de la visite</SelectItem>
                  <SelectItem value="apres_facture" data-testid="option-apres-facture">Après facture</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Type de rapport</Label>
            <Select value={form.requires_validation ? "with" : "direct"}
              onValueChange={(v) => set("requires_validation", v === "with")}>
              <SelectTrigger className="h-11" data-testid="workflow-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="with" data-testid="option-with-validation">Avec acceptation du client (ex. certificat PEB)</SelectItem>
                <SelectItem value="direct" data-testid="option-direct">Sans acceptation — définitif direct (ex. réception chaudière)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500">
              « Avec acceptation » : rapport provisoire → validation/remarques du client → rapport définitif.
              « Sans acceptation » : le client télécharge directement le rapport définitif dès le paiement.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="comment">Commentaire au client (inclus dans l'email du rapport)</Label>
            <Textarea id="comment" data-testid="comment-input" value={form.comment}
              onChange={(e) => set("comment", e.target.value)} rows={3} className="resize-none"
              placeholder="Ex. Le résultat est médiocre ; en me fournissant l'isolation de la toiture, il pourrait être amélioré." />
            <p className="text-xs text-slate-500">
              Ce message apparaîtra dans l'email envoyé avec le rapport <strong>provisoire</strong> et/ou <strong>définitif</strong>. Laissez vide si aucun commentaire.
            </p>
          </div>

          <label className="flex items-start gap-3 rounded-md border border-slate-200 p-4 cursor-pointer hover:bg-slate-50 transition-colors">
            <input type="checkbox" data-testid="send-code-email-checkbox" checked={form.send_code_email}
              onChange={(e) => set("send_code_email", e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-blue-600" />
            <span className="text-sm text-slate-700">
              <strong>Envoyer un email de bienvenue au client</strong>
              <span className="block text-slate-500 mt-0.5">
                Avec la facture Billit en pièce jointe si elle est liée. Décochez pour éviter un double email.
              </span>
            </span>
          </label>

          <div className="rounded-md bg-blue-50 border border-blue-100 p-4 text-sm text-blue-800">
            Dès que le <strong>paiement est détecté</strong>, le client reçoit automatiquement ses rapports PDF
            <strong>en pièces jointes</strong> par email — aucun code à saisir.
          </div>

          <Button type="submit" data-testid="create-dossier-submit" disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 h-11">
            <FloppyDisk size={18} weight="bold" className="mr-1.5" />
            {loading ? "Création…" : "Créer le dossier"}
          </Button>
        </form>

        <Dialog open={billitOpen} onOpenChange={setBillitOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Choisir la facture Billit</DialogTitle></DialogHeader>
            <Input placeholder="Rechercher (n° ou client)…" value={invSearch}
              onChange={(e) => setInvSearch(e.target.value)} data-testid="billit-search" className="mb-1" />
            <div className="max-h-96 overflow-y-auto space-y-2">
              {invLoading ? (
                <p className="text-sm text-slate-400 p-4 text-center">Chargement des factures…</p>
              ) : (() => {
                const list = invoices.filter((inv) =>
                  `${inv.order_number} ${inv.customer || ""}`.toLowerCase().includes(invSearch.toLowerCase()));
                if (list.length === 0)
                  return <p className="text-sm text-slate-400 p-4 text-center">Aucune facture trouvée.</p>;
                return list.map((inv) => (
                  <button key={inv.order_id} type="button" data-testid={`billit-invoice-${inv.order_id}`}
                    onClick={() => chooseInvoice(inv)}
                    className="w-full text-left border border-slate-200 rounded-md p-3 hover:border-blue-400 hover:bg-blue-50/40 transition-colors flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-sm text-slate-900 truncate">{inv.order_number} — {inv.customer || "—"}</p>
                      <p className="text-xs text-slate-500">
                        {typeof inv.total === "number" ? inv.total.toFixed(2) : inv.total} € · {(inv.order_date || "").slice(0, 10)}
                      </p>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded shrink-0 ${inv.paid ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                      {inv.paid ? "Payé" : "À payer"}
                    </span>
                  </button>
                ));
              })()}
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
